// CallPage (calls round): the call's own page, the layout. The caller said once in its title block (who,
// the customer and where; who's there today), a notice when there is one (Booked), then the work on the
// left (the booking, or after the call its reason) and the live transcript on the right in a narrower
// column that stays in view as the booking scrolls, divided by a hairline, never boxed. One column under
// 1100, the transcript after the booking over a rule. The call's state and its controls aren't here:
// they're on the strip above, on every page. `settle` is the second half of the one motion (DESIGN.md
// §9.4): the page comes in as the ringing row settles into your line; none with prefers-reduced-motion.
// The page is named by its title and described by its sub line (who, the match), so a screen reader
// hears both on arriving.
// Reads only --callpage-*.
import { useId, type ReactNode } from 'react';
import './CallPage.css';

export interface CallPageProps {
  /** Where it sits ("Inbox › Calls › On the call"). */
  crumb?: ReactNode;
  /** Who's calling ("Marguerite Osei", "New caller"). */
  title: ReactNode;
  /** The customer and where, one line ("Osei residence, 414 Cedar Hill Rd"). */
  sub?: ReactNode;
  /** Who's there today, when someone is ("Kenji O. is on his way, there by about 10:40"). */
  today?: ReactNode;
  /** Over the columns: what just happened (Booked), said once. */
  notice?: ReactNode;
  /** The booking, or after the call its reason. */
  main: ReactNode;
  /** The transcript. */
  side: ReactNode;
  /** The side column's name, for screen readers ("Transcript"). */
  sideLabel: string;
  /** Comes in with the strip's settle, as you answer. */
  settle?: boolean;
}

export function CallPage({ crumb, title, sub, today, notice, main, side, sideLabel, settle }: CallPageProps) {
  const h = useId();
  const d = useId();
  return (
    <article
      className="fl-callpage"
      data-settle={settle ? '' : undefined}
      aria-labelledby={h}
      aria-describedby={sub ? d : undefined}
    >
      {crumb ? <p className="fl-callpage__crumb">{crumb}</p> : null}
      <header className="fl-callpage__head">
        <h1 className="fl-callpage__title" id={h}>
          {title}
        </h1>
        {sub ? (
          <p className="fl-callpage__sub" id={d}>
            {sub}
          </p>
        ) : null}
        {today ? <p className="fl-callpage__today">{today}</p> : null}
      </header>
      {notice ? <div className="fl-callpage__notice">{notice}</div> : null}
      <div className="fl-callpage__cols">
        <div className="fl-callpage__main">{main}</div>
        <aside className="fl-callpage__side" aria-label={sideLabel}>
          {side}
        </aside>
      </div>
    </article>
  );
}
